import { ExternalLink } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { GetSupportaCampaignsQuery as GetSupportaCampaigns } from '__generated__/graphql';

import { NavItem, Section, SectionTitle } from '../../Common/Layout';
import { RegistrationSummary } from '../helpers';
import SupportaSymbol from '../../../common/Common/SupportaSymbol';
import UI from '../../../common/UI';
import useLocale from '../../../common/useLocale';

export interface PrivateFundraisingSectionProps {
  summary: RegistrationSummary;
  campaigns?: GetSupportaCampaigns['registrationSummary']['supporta_campaigns'];
}

const PrivateFundraisingSection = ({ summary, campaigns }: PrivateFundraisingSectionProps) => {
  const { t } = useTranslation();
  const { locale, formatCurrency } = useLocale();

  const { event } = summary;
  const exclusiveCharity = event.charities.length === 1 && !event.allow_other_charity ? event.charities[0] : null;

  const isCompany = summary.is_company || summary.event_participant.charity;

  return (
    <UI.GridContainer sc={{ gutter: 0.75 }}>
      <UI.Div>
        <SectionTitle>
          {t('your_fundraisers', { count: campaigns?.length || 1 })}
        </SectionTitle>
      </UI.Div>

      <UI.GridContainer sc={{ gutter: 0.5 }}>
        {!campaigns && (
          <Section>
            <UI.Div
              sc={{
                background: 'gray.50',
                flex: { alignItems: 'center', justifyContent: 'center' },
                loading: true,
                py: 0,
              }}
              style={{ minHeight: 72 }}
            >
              <UI.Loader sc={{ brand: 'gray.300' }} />
            </UI.Div>
          </Section>
        )}

        {campaigns?.map((campaign) => (
          <Section
            key={campaign.id}
          >
            <NavItem
              href={campaign.url}
              target="_blank"
              rel="noopener noreferrer"
              title={campaign.title}
              alignVertical="center"
              icon={<ExternalLink />}
              key={campaign.id}
            >
              <UI.GridContainer sc={{ columns: '1fr 70px', gutter: 0.5, alignVertical: 'center' }}>
                <UI.Div>
                  <UI.H4>
                    {campaign.title}
                  </UI.H4>
                  <UI.Span>
                    {t('fundraiser_summary', {
                      fundsRaised: formatCurrency(campaign.funds_raised),
                      charity: campaign.charity.title,
                    })}
                  </UI.Span>
                </UI.Div>
                <UI.Div>
                  {campaign.charity.logo_url && (
                    <img
                      src={campaign.charity.logo_url}
                      style={{ maxWidth: 70, maxHeight: 40, display: 'block', margin: '0 auto' }}
                      alt={campaign.charity.title}
                    />
                  )}
                </UI.Div>
              </UI.GridContainer>
            </NavItem>
          </Section>
        ))}

        {campaigns?.length === 0 && !event.require_fundraising && !isCompany && (
          <Section>
            <NavItem
              href={`${event.supporta_url}?locale=${locale}`}
              target="_blank"
              rel="noopener noreferrer"
              title={t('create_fundraiser')}
              alignVertical="center"
              icon={<ExternalLink />}
            >
              <UI.GridContainer sc={{ columns: '40px 1fr', alignVertical: 'center', gutter: 0.5 }}>
                <UI.ProfilePic
                  sc={{
                    size: 40,
                    background: !exclusiveCharity?.logo_url ? 'supporta_emerald' : 'white',
                    color: 'white',
                  }}
                >
                  {exclusiveCharity?.logo_url ? (
                    <img src={exclusiveCharity.logo_url} alt={exclusiveCharity.title} />
                  ) : (
                    <UI.Icon sc={{ size: 1.5 }}>
                      <SupportaSymbol />
                    </UI.Icon>
                  )}
                </UI.ProfilePic>
                <UI.Div>
                  <UI.H4>
                    {t('create_fundraiser')}
                  </UI.H4>
                  {exclusiveCharity ? exclusiveCharity.title : t('raise_money_for_charity')}
                </UI.Div>
              </UI.GridContainer>
            </NavItem>
          </Section>
        )}

        {campaigns?.length === 0 && (isCompany || event.require_fundraising) && (
          <Section>
            <UI.Div sc={{ color: 'gray.500' }}>
              {t('no_personal_fundraiser_yet')}
              {' '}
              {!event.require_fundraising && (
                <UI.A
                  href={`${event.supporta_url}?locale=${locale}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('create_fundraiser')}
                  {' '}
                  <UI.Icon>
                    <ExternalLink />
                  </UI.Icon>
                </UI.A>
              )}
            </UI.Div>
          </Section>
        )}
      </UI.GridContainer>
    </UI.GridContainer>
  );
};

export default PrivateFundraisingSection;
